import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, MapPin, Check } from "lucide-react";
import { API } from "@/App";
import { useTranslation } from "@/i18n/translations";
import PhoneInput, { guessCountry } from "@/components/PhoneInput";
import BookingSuccess from "@/components/BookingSuccess";

// Public booking wizard for mini-sites (Planity-like, one screen per step):
//  online:  [branch] → service → master → date+time → contacts → success
//  lead mode (calendar disabled): service → contacts (+ desired time) → success
export default function MiniSiteBooking({ cfg, salonId, accent, visitorLang, salonName, salonPhone, whatsapp, tr, privacy }) {
  const { t } = useTranslation();
  // Display-only translations (raw names are still sent to the API)
  const svcLabel = (s) => (s && tr?.services?.[s.id]?.name) || s?.name || "";
  const catLabel = (cat) => (cat && tr?.categories?.[cat]) || cat;
  const levelLabel = (st) => (st && tr?.levels?.[st.level]) || st?.level_name || "";
  const tf = (key, fb, params) => { const v = t(key, params); return v && v !== key ? v : fb; };
  const online = !!cfg?.online_booking;
  const branches = cfg?.branches || [];
  const needBranch = online && branches.length > 1;
  const [branchId, setBranchId] = useState(needBranch ? "" : (branches.find((b) => b.is_default)?.id || branches[0]?.id || ""));
  const [service, setService] = useState("");
  const [staffId, setStaffId] = useState("any");
  const [staffPinned, setStaffPinned] = useState(false);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [time, setTime] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState({ e164: "", valid: false, formatted: "", digits: "" });
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [leadDate, setLeadDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const [openCat, setOpenCat] = useState(null);
  const [expandedCats, setExpandedCats] = useState({});
  const [dayStatus, setDayStatus] = useState({});
  const SVC_PAGE = 5;

  const steps = useMemo(() => {
    const s = online ? [...(needBranch ? ["branch"] : []), "service", ...(staffPinned ? [] : ["master"]), "time", "contact"] : ["service", "contact"];
    return s;
  }, [online, needBranch, staffPinned]);
  const [step, setStep] = useState(steps[0]);
  const stepIdx = Math.max(0, steps.indexOf(step));
  const mounted = useRef(false);
  const go = (s) => { setStep(s); setError(""); };
  const back = () => stepIdx > 0 && go(steps[stepIdx - 1]);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    document.getElementById("ms-booking")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);

  const defaultCountry = useMemo(() => guessCountry({
    phones: [salonPhone, whatsapp, ...branches.map((b) => b.phone)], timezone: cfg?.timezone,
  }), [salonPhone, whatsapp, branches, cfg?.timezone]);

  const days = useMemo(() => Array.from({ length: 14 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() + i);
    return {
      iso: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
      wd: d.toLocaleDateString(visitorLang || "en", { weekday: "short" }),
      dm: d.toLocaleDateString(visitorLang || "en", { day: "numeric", month: "short" }),
    };
  }), [visitorLang]);

  // HARD RULE (mirrors backend): a master does only the services ticked on their card. Strict as soon as any master has a list.
  const strictServices = useMemo(() => (cfg?.staff || []).some((s) => (s.services || []).length), [cfg]);
  const norm = (x) => String(x || "").trim().toLowerCase().replace(/\s+/g, " ");
  const doesService = (s, svcName) => {
    if (!svcName || !strictServices) return true;
    const mine = [...(s.services || []), ...Object.keys(s.service_overrides || {})].map(norm);
    return mine.includes(norm(svcName));
  };
  const branchStaff = useMemo(() => (cfg?.staff || []).filter(
    (s) => !branchId || !(s.branch_ids || []).length || s.branch_ids.includes(branchId)), [cfg, branchId]);
  const staffForService = useMemo(() => branchStaff.filter((s) => doesService(s, service)), [branchStaff, service]);
  const selectedStaff = (cfg?.staff || []).find((s) => s.id === staffId) || null;
  const visibleServices = useMemo(() => (cfg?.services || []).filter(
    (s) => (!branchId || !(s.branch_ids || []).length || s.branch_ids.includes(branchId))
      && (!staffPinned || !selectedStaff || doesService(selectedStaff, s.name))
      // online booking: hide services that no master of this branch performs
      && (!online || !strictServices || branchStaff.some((st) => doesService(st, s.name)))),
    [cfg, selectedStaff, branchId, staffPinned, online, strictServices, branchStaff]);
  useEffect(() => {
    if (staffId !== "any" && service && !staffForService.some((s) => s.id === staffId)) { setStaffId("any"); setStaffPinned(false); }
  }, [staffForService, staffId, service]);

  const ovFor = (s, st) => { const ov = st?.service_overrides || {}; const k = Object.keys(ov).find((x) => x.toLowerCase() === (s?.name || "").toLowerCase()); return k ? ov[k] || {} : {}; };
  const durFor = (s, st) => ovFor(s, st).duration_min || s?.duration_min;
  const priceFor = (s, st) => {
    if (!s) return null;
    if (ovFor(s, st).price != null) return ovFor(s, st).price;
    const lp = s.level_prices || {};
    if (st && st.level && lp[st.level] != null) return lp[st.level];
    const bp = s.branch_prices || {};
    const bid = branchId || (st && (st.branch_ids || []).length === 1 ? st.branch_ids[0] : null);
    if (bid && bp[bid] != null) return bp[bid];
    return s.price;
  };
  const cur = cfg?.currency || "€";
  const priceLabel = (s, st) => {
    if (!s) return "";
    if (st) return priceFor(s, st) != null ? `${cur}${priceFor(s, st)}` : "";
    const base = priceFor(s, null);
    const variants = new Set([base, ...Object.values(s.level_prices || {})].filter((v) => v != null));
    if (!variants.size) return "";
    return variants.size > 1 ? `${tf("ms.book.from", "от")} ${cur}${Math.min(...variants)}` : `${cur}${base}`;
  };

  // "Записаться" on a team card preselects that master and skips the master step
  useEffect(() => {
    const h = (e) => {
      const st = (cfg?.staff || []).find((s) => s.id === e.detail?.staffId);
      if (!st) return;
      if ((st.branch_ids || []).length && !st.branch_ids.includes(branchId)) setBranchId(st.branch_ids[0]);
      else if (!branchId && needBranch) setBranchId(branches.find((b) => b.is_default)?.id || branches[0]?.id || "");
      setStaffId(st.id); setStaffPinned(true); setDone(null);
      go(service ? "time" : "service");
    };
    window.addEventListener("chatity_ms_pick_staff", h);
    return () => window.removeEventListener("chatity_ms_pick_staff", h);
  }, [cfg, branchId, service, needBranch, branches]);

  // Day strip: closed (salon/master schedule) vs fully booked vs available; auto-jump to the first open day
  useEffect(() => {
    if (!online || !service || step !== "time") return;
    let cancelled = false;
    axios.get(`${API}/public/booking/${salonId}/days`, {
      params: { service, staff_id: staffId === "any" ? undefined : staffId, branch_id: branchId || undefined, days: 14 },
    }).then((r) => {
      if (cancelled) return;
      const map = Object.fromEntries((r.data.days || []).map((d) => [d.date, d.status]));
      setDayStatus(map);
      if (map[date] && map[date] !== "available") {
        const first = (r.data.days || []).find((d) => d.status === "available");
        if (first) setDate(first.date);
      }
    }).catch(() => { if (!cancelled) setDayStatus({}); });
    return () => { cancelled = true; };
  }, [online, service, staffId, branchId, salonId, step]);

  useEffect(() => {
    if (!online || !service || !date || step !== "time") return;
    let cancelled = false;
    setLoadingSlots(true); setTime("");
    axios.get(`${API}/public/booking/${salonId}/slots`, {
      params: { service, date, staff_id: staffId === "any" ? undefined : staffId, branch_id: branchId || undefined },
    }).then((r) => { if (!cancelled) setSlots(r.data.slots || []); })
      .catch(() => { if (!cancelled) setSlots([]); })
      .finally(() => { if (!cancelled) setLoadingSlots(false); });
    return () => { cancelled = true; };
  }, [online, service, date, staffId, branchId, salonId, step]);

  const svc = (cfg?.services || []).find((s) => s.name === service);
  const branch = branches.find((b) => b.id === branchId) || null;
  const slotStaffList = time ? (slots.find((s) => s.time === time)?.staff || []).map((x) => (cfg?.staff || []).find((s) => s.id === x.id)).filter(Boolean) : [];
  const regularInSlot = slotStaffList.find((s) => !s.accepts_lower_levels);
  const fallbackOnly = !selectedStaff && slotStaffList.length > 0 && !regularInSlot && slotStaffList.some((s) => s.accepts_lower_levels && s.level);
  const effectiveStaff = selectedStaff || regularInSlot || slotStaffList[0] || null;
  const finalPrice = svc && online ? (fallbackOnly ? `${cur}${priceFor(svc, null)}` : priceLabel(svc, effectiveStaff)) : (svc ? priceLabel(svc, null) : "");

  const submit = async () => {
    setError(""); setBusy(true);
    try {
      if (online) {
        const slot = slots.find((s) => s.time === time);
        const chosenStaff = staffId !== "any" ? staffId : slot?.staff?.[0]?.id;
        const r = await axios.post(`${API}/public/booking/${salonId}/book`, {
          service, date, time, staff_id: chosenStaff, branch_id: branchId || null,
          client_name: name, phone: phone.e164, lang: visitorLang || "en",
          ...(privacy?.marketing_enabled ? { marketing_consent: marketingConsent, consent_text: marketingText } : {}),
        });
        setDone(r.data);
      } else {
        const r = await axios.post(`${API}/public/booking/${salonId}/request`, {
          service, preferred_date: leadDate, client_name: name, phone: phone.e164, lang: visitorLang || "en",
          ...(privacy?.marketing_enabled ? { marketing_consent: marketingConsent, consent_text: marketingText } : {}),
        });
        setDone({ lead: true, ...r.data });
      }
    } catch (e) {
      if (e?.response?.status === 409) {
        setError(tf("ms.book.taken", "Это время только что заняли — выберите другое"));
        setTime(""); go("time");
      } else {
        setError(e?.response?.data?.detail || "Error");
      }
    } finally { setBusy(false); }
  };

  const reset = () => { setDone(null); setTime(""); setName(""); setPhone({ e164: "", valid: false, formatted: "", digits: "" }); setMarketingConsent(false); setStaffPinned(false); setStaffId("any"); setService(""); setStep(steps[0]); };
  const canSubmit = name.trim() && phone.digits.length >= 6 && (online ? (service && time) : !!service);
  const controllerName = privacy?.controller_name || salonName || "";
  const privacyUrl = privacy?.privacy_policy_url || "/privacy";
  const marketingText = tf("ms.book.marketingConsent", "Я согласен(на) получать предложения и новости {business} по e-mail, SMS или в мессенджерах", { business: controllerName });

  const rowCls = (active) => `w-full flex items-center gap-3 text-left rounded-xl border px-3.5 py-3 transition-colors ${active ? "bg-gray-50" : "bg-white border-gray-200 hover:border-gray-400 active:bg-gray-50"}`;
  const rowStyle = (active) => (active ? { borderColor: accent } : {});
  const chipCls = (active) => `px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${active ? "text-white" : "bg-white text-gray-800 border-gray-200 hover:border-gray-400"}`;
  const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white text-gray-900";
  const timeLabel = `${days.find((d) => d.iso === date)?.dm || date}${time ? ` · ${time}` : ""}`;
  const masterLabel = selectedStaff ? selectedStaff.name : tf("ms.book.anyMaster", "Любой мастер");

  if (done) {
    return (
      <BookingSuccess done={done} tf={tf} lang={visitorLang} accent={accent} salonName={salonName} currency={cur}
        address={branches.find((b) => b.id === (done.branch_id || branchId))?.address || branch?.address || ""}
        waTarget={whatsapp || salonPhone || branch?.phone || ""}
        phoneLabel={phone.formatted ? tf("ms.book.reminderTo", "Напоминание придёт на {phone}", { phone: phone.formatted }) : ""}
        onAgain={reset} />
    );
  }

  const titles = {
    branch: tf("ms.book.chooseBranch", "Выберите филиал"), service: tf("ms.book.chooseService", "Выберите услугу"),
    master: tf("ms.book.chooseMaster", "Выберите мастера"), time: tf("ms.book.chooseTime", "Выберите дату и время"),
    contact: tf("ms.book.contact", "Ваши контакты"),
  };
  const crumbs = [
    needBranch && branch && stepIdx > steps.indexOf("branch") && { k: "branch", label: branch.name },
    service && stepIdx > steps.indexOf("service") && { k: "service", label: svcLabel(svc) || service },
    online && !staffPinned && stepIdx > steps.indexOf("master") && { k: "master", label: masterLabel },
    online && staffPinned && selectedStaff && { k: "service", label: selectedStaff.name },
    online && time && stepIdx > steps.indexOf("time") && { k: "time", label: timeLabel },
  ].filter(Boolean);

  return (
    <section id="ms-booking" className="ms-fade bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-5 mb-4 scroll-mt-4" data-testid="minisite-booking">
      <div className="flex items-center gap-2 mb-1">
        {stepIdx > 0 ? (
          <button type="button" onClick={back} className="p-1.5 -ml-1.5 rounded-full hover:bg-gray-100" aria-label={tf("ms.book.back", "Назад")} data-testid="minisite-book-back">
            <ChevronLeft size={20} />
          </button>
        ) : <span className="text-lg">📅</span>}
        <h2 className="text-lg md:text-xl font-bold flex-1 truncate" data-testid="minisite-book-step-title">
          {online ? titles[step] : (step === "service" ? titles.service : tf("ms.book.leadTitle", "Оставить заявку"))}
        </h2>
        <span className="text-xs font-semibold text-gray-400 shrink-0" data-testid="minisite-book-step-counter">
          {tf("ms.book.stepOf", "Шаг {n} из {total}", { n: stepIdx + 1, total: steps.length })}
        </span>
      </div>
      <div className="flex gap-1 mb-3" aria-hidden>
        {steps.map((s, i) => <span key={s} className="h-1 flex-1 rounded-full" style={{ backgroundColor: i <= stepIdx ? accent : "#e5e7eb" }} />)}
      </div>
      {crumbs.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="minisite-book-crumbs">
          {crumbs.map((c, i) => (
            <button key={`${c.k}-${i}`} type="button" onClick={() => go(c.k)}
              className="text-xs px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-400 max-w-full truncate" data-testid={`minisite-book-crumb-${c.k}`}>
              {c.label}
            </button>
          ))}
        </div>
      )}

      {step === "branch" && (
        <div className="space-y-2" data-testid="minisite-book-branches">
          {branches.map((b) => (
            <button key={b.id} type="button" className={rowCls(branchId === b.id)} style={rowStyle(branchId === b.id)}
              onClick={() => { setBranchId(b.id); setStaffId("any"); setStaffPinned(false); go("service"); }} data-testid={`minisite-book-branch-${b.id}`}>
              <MapPin size={18} className="shrink-0" style={{ color: accent }} />
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-sm text-gray-900">{b.name}</span>
                {b.address && <span className="block text-xs text-gray-500 truncate">{b.address}</span>}
              </span>
              <ChevronRight size={16} className="text-gray-400 shrink-0" />
            </button>
          ))}
        </div>
      )}

      {step === "service" && (
        <div className="space-y-4" data-testid="minisite-book-services">
          {visibleServices.length === 0 && (
            <p className="text-sm text-gray-500 py-3" data-testid="minisite-book-noservices">{tf("ms.book.noServicesHere", "Для онлайн-записи здесь пока нет услуг — напишите нам в чат, и мы подберём время")}</p>
          )}
          {(() => {
            const groups = Object.entries(visibleServices.reduce((acc, s) => { (acc[s.category || ""] = acc[s.category || ""] || []).push(s); return acc; }, {}));
            const hasCats = groups.some(([cat]) => cat);
            const otherLabel = tf("ms.book.otherCategory", "Другое");
            const renderList = (cat, list) => {
              const expanded = !!expandedCats[cat];
              const shown = expanded ? list : list.slice(0, SVC_PAGE);
              return (
                <div className="space-y-2">
                  {shown.map((s) => (
                    <button key={s.id || s.name} type="button" className={rowCls(service === s.name)} style={rowStyle(service === s.name)}
                      onClick={() => { setService(s.name); go(online ? (staffPinned ? "time" : "master") : "contact"); }}
                      data-testid={`minisite-book-service-${s.id || s.name}`}>
                      <span className="flex-1 min-w-0">
                        <span className="block font-semibold text-sm text-gray-900">{svcLabel(s)}</span>
                        <span className="block text-xs text-gray-500">{durFor(s, staffPinned ? selectedStaff : null)} {tf("ms.book.minutes", "мин")}</span>
                      </span>
                      <span className="text-sm font-bold text-gray-900 shrink-0">{priceLabel(s, staffPinned ? selectedStaff : null)}</span>
                      <ChevronRight size={16} className="text-gray-400 shrink-0" />
                    </button>
                  ))}
                  {list.length > SVC_PAGE && (
                    <button type="button" onClick={() => setExpandedCats({ ...expandedCats, [cat]: !expanded })}
                      className="w-full py-2 rounded-lg text-xs font-bold text-gray-600 bg-gray-50 hover:bg-gray-100 transition-colors"
                      data-testid={`minisite-book-services-more-${cat || "_"}`}>
                      {expanded ? tf("miniSite.services.collapse", "Свернуть") : `${tf("miniSite.services.viewAll", "Показать все")} (${list.length})`}
                    </button>
                  )}
                </div>
              );
            };
            if (!hasCats) return renderList("", visibleServices);
            return groups.map(([cat, list], ci) => {
              const open = openCat === null ? ci === 0 : openCat === cat;
              return (
                <div key={cat || "_"} className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`minisite-book-cat-${ci}`}>
                  <button type="button" onClick={() => setOpenCat(open ? "" : cat)} data-testid={`minisite-book-cat-toggle-${ci}`}
                    className="w-full flex items-center justify-between px-3.5 py-3 text-left hover:bg-gray-50">
                    <span className="font-bold text-sm text-gray-900">{catLabel(cat) || otherLabel} <span className="ml-1 text-xs font-normal text-gray-500">{list.length}</span></span>
                    <ChevronRight size={16} className={`text-gray-400 transition-transform ${open ? "rotate-90" : ""}`} />
                  </button>
                  {open && <div className="px-3 pb-3">{renderList(cat, list)}</div>}
                </div>
              );
            });
          })()}
        </div>
      )}

      {step === "master" && (
        <div className="space-y-2" data-testid="minisite-book-masters">
          <button type="button" className={rowCls(staffId === "any")} style={rowStyle(staffId === "any")}
            onClick={() => { setStaffId("any"); go("time"); }} data-testid="minisite-book-master-any">
            <span className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 shrink-0">✦</span>
            <span className="flex-1 min-w-0">
              <span className="block font-semibold text-sm text-gray-900">{tf("ms.book.anyMaster", "Любой мастер")}</span>
              <span className="block text-xs text-gray-500">{tf("ms.book.anyMasterHint", "Запишем к свободному мастеру на выбранное время")}</span>
            </span>
            <span className="text-sm font-bold text-gray-900 shrink-0">{priceLabel(svc, null)}</span>
            <ChevronRight size={16} className="text-gray-400 shrink-0" />
          </button>
          {staffForService.map((s) => (
            <button key={s.id} type="button" className={rowCls(staffId === s.id)} style={rowStyle(staffId === s.id)}
              onClick={() => { setStaffId(s.id); go("time"); }} data-testid={`minisite-book-master-${s.id}`}>
              {s.photo_url
                ? <img src={s.photo_url} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                : <span className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shrink-0" style={{ backgroundColor: s.color || accent }}>{(s.name || "?")[0]}</span>}
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-sm text-gray-900">{s.name}</span>
                <span className="block text-xs text-gray-500">
                  {levelLabel(s) ? `${levelLabel(s)} · ` : ""}{durFor(svc, s)} {tf("ms.book.minutes", "мин")}
                </span>
              </span>
              <span className="text-sm font-bold text-gray-900 shrink-0">{priceLabel(svc, s)}</span>
              <ChevronRight size={16} className="text-gray-400 shrink-0" />
            </button>
          ))}
        </div>
      )}

      {step === "time" && (
        <>
          <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-1 px-1" data-testid="minisite-book-dates">
            {days.map((d) => {
              const st = dayStatus[d.iso];
              const closed = st === "closed";
              const full = st === "full";
              const active = date === d.iso;
              return (
                <button key={d.iso} type="button" onClick={() => !closed && setDate(d.iso)} disabled={closed}
                  className={`${chipCls(active)} shrink-0 flex flex-col items-center leading-tight ${closed ? "opacity-40 cursor-not-allowed line-through decoration-gray-400" : ""} ${full && !active ? "text-gray-400" : ""}`}
                  style={active ? { backgroundColor: accent, borderColor: accent } : {}}
                  title={closed ? tf("ms.book.dayClosed", "Выходной") : full ? tf("ms.book.dayFull", "Мест нет") : ""}
                  data-status={st || ""}
                  data-testid={`minisite-book-day-${d.iso}`}>
                  <span className="text-[10px] uppercase opacity-80">{d.wd}</span>
                  <span>{d.dm}</span>
                </button>
              );
            })}
          </div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">{tf("ms.book.time", "Время")}</label>
          {loadingSlots ? (
            <div className="text-sm text-gray-400 py-3">…</div>
          ) : slots.length ? (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-2" data-testid="minisite-book-slots">
              {slots.map((s) => (
                <button key={s.time} type="button" onClick={() => { setTime(s.time); go("contact"); }}
                  className={`${chipCls(time === s.time)} text-center`}
                  style={time === s.time ? { backgroundColor: accent, borderColor: accent } : {}}
                  data-testid={`minisite-book-slot-${s.time}`}>
                  {s.time}
                </button>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-400 py-3" data-testid="minisite-book-noslots">
              {dayStatus[date] === "closed" ? tf("ms.book.dayClosedLong", "В этот день салон не работает") : tf("ms.book.noSlots", "Нет свободного времени в этот день")}
            </div>
          )}
        </>
      )}

      {step === "contact" && (
        <div className="space-y-3">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3.5 text-sm space-y-1" data-testid="minisite-book-summary">
            <div className="font-semibold text-gray-900">{svcLabel(svc) || service}</div>
            {online && (
              <>
                <div className="text-gray-700">{timeLabel} · {effectiveStaff ? effectiveStaff.name : masterLabel}</div>
                {fallbackOnly && (
                  <p className="text-xs text-gray-500" data-testid="minisite-book-fallback-note">
                    {tf("ms.book.fallbackNote", "На это время свободен только мастер более высокого уровня — запишем по обычной цене")}: {slotStaffList[0].name} ({levelLabel(slotStaffList[0])})
                  </p>
                )}
                {branch?.address && <div className="text-xs text-gray-500">📍 {branch.address}</div>}
              </>
            )}
            {finalPrice && <div className="text-gray-900 font-bold">{tf("ms.book.total", "Итого")}: {finalPrice}</div>}
          </div>
          {!online && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">{tf("ms.book.leadDate", "Желаемые дата и время")}</label>
              <input value={leadDate} onChange={(e) => setLeadDate(e.target.value)} placeholder="15.10 после 16:00" className={inputCls} data-testid="minisite-book-leaddate" />
            </div>
          )}
          <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name"
            placeholder={tf("ms.book.name", "Ваше имя")} className={inputCls} data-testid="minisite-book-name" />
          <div>
            <PhoneInput value={phone.e164} onChange={setPhone} defaultCountry={defaultCountry} lang={visitorLang || "en"}
              accent={accent} placeholder={tf("ms.book.phone", "Телефон")} testId="minisite-book-phone" />
            <p className={`text-xs mt-1.5 ${phone.digits.length >= 6 && !phone.valid ? "text-amber-600" : "text-gray-500"}`} data-testid="minisite-book-phone-hint">
              {phone.digits.length >= 6 && !phone.valid
                ? tf("ms.book.phoneLooksWrong", "Похоже, номер неполный — проверьте, пожалуйста")
                : tf("ms.book.phoneCheck", "Проверьте номер — на него придёт напоминание о визите")}
              {phone.formatted ? <> · <b className="text-gray-800">{phone.formatted}</b></> : null}
            </p>
          </div>
          {/* GDPR: booking is contract-based processing → information notice, no mandatory consent checkbox */}
          <p className="text-xs text-gray-500 leading-relaxed" data-testid="minisite-book-privacy-notice">
            {tf("ms.book.privacyNotice", "Нажимая «Записаться», вы передаёте свои данные {business} для обработки заявки и управления вашим визитом (включая напоминания). Chatity — технический провайдер и обрабатывает данные по поручению бизнеса.", { business: controllerName })}{" "}
            <a href={privacyUrl} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: accent }} data-testid="minisite-book-privacy-link">
              {tf("ms.book.learnMore", "Подробнее")}
            </a>
          </p>
          {privacy?.marketing_enabled && (
            <label className="flex items-start gap-2.5 text-xs text-gray-600 cursor-pointer select-none" data-testid="minisite-book-marketing-label">
              <span className="relative mt-0.5 shrink-0">
                <input type="checkbox" checked={marketingConsent} onChange={(e) => setMarketingConsent(e.target.checked)} className="peer sr-only" data-testid="minisite-book-marketing-consent" />
                <span className="block w-5 h-5 rounded border border-gray-400 bg-white peer-checked:border-transparent peer-focus-visible:ring-2" style={marketingConsent ? { backgroundColor: accent } : {}} />
                {marketingConsent && <Check size={14} className="absolute inset-0 m-auto text-white pointer-events-none" />}
              </span>
              <span>{marketingText} <span className="text-gray-400">({tf("ms.book.optional", "необязательно")})</span></span>
            </label>
          )}
          <button type="button" onClick={submit} disabled={busy || !canSubmit}
            className="w-full rounded-lg px-4 py-3 text-sm font-bold text-white transition-opacity hover:opacity-85 disabled:opacity-40"
            style={{ backgroundColor: accent }} data-testid="minisite-book-submit">
            {busy ? "…" : tf("ms.book.submit", "Записаться")}{finalPrice ? ` · ${finalPrice}` : ""}
          </button>
          {error && <p className="text-sm text-red-600" data-testid="minisite-book-error">{error}</p>}
        </div>
      )}
      {error && step !== "contact" && <p className="text-sm text-red-600 mt-2" data-testid="minisite-book-error">{error}</p>}
    </section>
  );
}
